import {
  Uploader,
  formatSize,
  createUploader,
  UploadMetadata,
  EnhancedProgressEvent
} from '@ks/uploader';
import intl from '@/internationalzation/intl';
import { v4 } from 'uuid';
import { CustomViewer } from '@src/components/pc/native-img-preview';

// KIM的图片显示逻辑
// 处理图片的加载，根据原始宽高和盒子宽度
const IMAGE_MAX_HEIGHT = 320;
const IMAGE_MAX_WIDTH = 180;
const MAX_PREVIEW_SIZE = 1060;
const DEFAULT_RATIO = IMAGE_MAX_WIDTH / IMAGE_MAX_HEIGHT;
const MIN_SIZE = 40;
// 极端图形的比例
const MAX_RATIO = 15;
export interface ProgressData {
  id: string;
  type: string;
  size: number;
  name: string;
  total: string;
  loaded: string;
  percent: number;
  speed: string;
  isError: boolean;
  isFinish: boolean;
  link: string;
  isCancel: boolean;
  resourceId: string; // 服务端给的得带回去
  errorMessage?: string;
}
export const initProgressData: ProgressData = {
  id: '1',
  type: '',
  size: 0,
  name: '',
  total: '0byte',
  loaded: '0byte',
  percent: 0,
  speed: '0byte/ms',
  isError: false,
  isFinish: false,
  resourceId: '',
  link: '',
  isCancel: false
};
export interface UploadOptions<T> {
  concurrent: number;
  validate: (file: File) => {
    success: boolean;
    message: string;
  };
  getTokenApi: (extra?: T) => Promise<UploadMetadata>;
  finishUploadApi: (
    params: {
      fileName: string;
      fileSize: number;
      fileType: string;
      uploadToken: string;
    },
    extra?: T
  ) => Promise<{
    isOk: boolean;
    data: {
      url: string;
      resourceId: string;
    };
  }>;
}
export default class FileUpload<ExtraParams> {
  private uploadInstance: Uploader<UploadMetadata>;
  private options: UploadOptions<ExtraParams>;
  static MAX_TIMEOUT = 1000 * 5;
  constructor(options: UploadOptions<ExtraParams>) {
    this.options = options;
    this.uploadInstance = createUploader({
      shouldFileSplit: 10 * 1024 * 1024
    });
  }
  static getUniqueId = () => {
    return v4();
  };
  static getFileSuffix = (name: string) => {
    return name.split('.').pop() || 'wz';
  };
  static isImage = (type: string) => {
    return type.startsWith('image/');
  };
  static getLocalImgLink = (file: File): Promise<string> => {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        resolve(e.target?.result as string);
      };
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  };
  static getImageOriginSizeByUrl = (wrapWidth: number, link: string) => {
    let url = null;
    try {
      url = new URL(link);
    } catch (e) {
      console.info(link, 'link');
      console.error(e);
    }
    const originSize = (url && url.searchParams.get('origin-size')) || '';
    const regex = /w_(\d+),h_(\d+)/; // \d+ 匹配一个或多个数字
    const match = originSize.match(regex) || [];
    if (match[1] && match[2]) {
      return FileUpload.getKimPicRatio(wrapWidth, Number(match[1]), Number(match[2]));
    }
    return FileUpload.getKimPicRatio(wrapWidth, 0, 0);
  };
  static getImageOriginSize = (
    link: string
  ): Promise<{
    width: number;
    height: number;
  }> => {
    return new Promise((res, rej) => {
      const image = new Image();
      image.onload = (event: Event) => {
        const { naturalHeight, naturalWidth } = event.currentTarget as HTMLImageElement;
        res({
          width: naturalWidth,
          height: naturalHeight
        });
      };
      image.onerror = e => rej(e);
      image.src = link;
    });
  };
  static getKimPicRatio = (wrapWidth = IMAGE_MAX_WIDTH, realWidth: number, realHeight: number) => {
    // 拿不到宽高的处理
    if (!realWidth || !realHeight) {
      return {
        width: `${IMAGE_MAX_WIDTH}px`,
        height: `${IMAGE_MAX_WIDTH}px`
      };
    }
    const ratio = realWidth / realHeight;
    // 极端图片处理-长图
    if (ratio < 1 / MAX_RATIO) {
      return {
        width: `${IMAGE_MAX_WIDTH}px`,
        height: `${IMAGE_MAX_HEIGHT}px`
      };
    }
    // 极端图片处理-宽图
    if (ratio > MAX_RATIO) {
      return {
        width: `${wrapWidth}px`,
        height: `${MIN_SIZE}px`
      };
    }
    // 长图
    if (ratio < DEFAULT_RATIO) {
      const _realHeight = realHeight > MAX_PREVIEW_SIZE ? MAX_PREVIEW_SIZE : realHeight;
      const height = _realHeight > IMAGE_MAX_HEIGHT ? IMAGE_MAX_HEIGHT : _realHeight;
      const width = height * ratio;
      if (width < MIN_SIZE) {
        const new_height = MIN_SIZE / ratio;
        const _height = new_height > IMAGE_MAX_HEIGHT ? IMAGE_MAX_HEIGHT : new_height;
        return {
          width: `${MIN_SIZE}px`,
          height: `${_height}px`
        };
      }
      return {
        width: `${width}px`,
        height: `${height}px`
      };
    }
    // 宽图
    const _realWidth = realWidth > MAX_PREVIEW_SIZE ? MAX_PREVIEW_SIZE : realWidth;
    const width = _realWidth > wrapWidth ? wrapWidth : _realWidth;
    const height = width / ratio;
    if (height < MIN_SIZE) {
      const new_width = MIN_SIZE * ratio;
      const _width = new_width > IMAGE_MAX_WIDTH ? IMAGE_MAX_WIDTH : new_width;
      return {
        width: `${_width}px`,
        height: `${MIN_SIZE}px`
      };
    }
    return {
      width: `${width}px`,
      height: `${height}px`
    };
  };
  static imageAddOriginSize = async (link: string) => {
    const url = new URL(link);
    const { width, height } = await FileUpload.getImageOriginSize(link);
    url.searchParams.append('origin-size', `w_${width},h_${height}`);
    return url.toString();
  };
  static getInitProgressData = (id: string, file: File, initLink?: string): ProgressData => {
    return {
      ...initProgressData,
      id,
      type: file.type || '',
      link: initLink || '',
      size: file.size,
      name: file.name
    };
  };
  static openImage = (url: string) => {
    const $ul = document.createElement('ul');
    const $li = document.createElement('li');
    const $img = document.createElement('img');
    $img.setAttribute('data-src', url);
    $ul.append($li);
    $li.append($img);
    const view: CustomViewer = new CustomViewer(
      $ul,
      {
        filter: (image: HTMLImageElement) => {
          return image.getAttribute('data-src');
        },
        url: (image: HTMLImageElement) => {
          return image.getAttribute('data-src');
        },
        hidden: () => view.destroy(),
        show: () => {}
      },
      true
    );
    view.view(0);
  };
  private onProgressHandle = (
    id: string,
    event: EnhancedProgressEvent<UploadMetadata>,
    onProgress: (id: string, status: Partial<ProgressData>) => void
  ) => {
    if (!event.lengthComputable) {
      return;
    }
    if (event.type === 'progress') {
      onProgress(id, {
        total: formatSize(event.total, 2),
        loaded: formatSize(event.loaded, 2),
        speed: formatSize(event.speed, 2),
        percent: event.percent
      });
    }
  };
  public cancelUpload = (id: string) => {
    this.uploadInstance.cancel(id);
  };
  static getErrorMessage = (e: any) => {
    if (e.message === 'token feedback is fail') {
      return intl.t('上传失败，接口异常');
    }
    if (e.message.startsWith('Cancel')) {
      return intl.t('上传失败，取消上传');
    }
    return intl.t('上传失败，网络超时');
  };

  // 自动调起系统文件选择
  static triggerFileSelect = ({
    type,
    multiple = false,
    beforeUpload,
    onSelect,
    onUpload
  }: {
    type: 'image' | 'file' | 'capture';
    multiple?: boolean;
    beforeUpload?: (file: File) => boolean;
    onSelect?: (file: File[]) => void;
    onUpload: (file: File[]) => void;
  }) => {
    // 创建 input 元素
    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none'; // 防止闪烁
    input.multiple = multiple;

    // 设置文件类型
    switch (type) {
      case 'image':
        input.accept = 'image/kim';
        break;
      case 'capture':
        input.accept = 'image/kim';
        input.capture = 'true';
        break;
      case 'file':
      default:
        input.accept = '*/*';
        break;
    }

    // 添加到 body
    document.body.appendChild(input);
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement;
      let files = target.files ? Array.from(target.files) : [];
      if (files.length === 0) {
        cleanup();
        return;
      }

      // 过滤文件：beforeUpload
      if (beforeUpload) {
        const results = await Promise.all(
          files.map(async file => {
            const result = await beforeUpload(file);
            return result ? file : null;
          })
        );
        files = results.filter((f): f is File => !!f);
      }

      // 回调选中文件
      if (onSelect) {
        onSelect(files);
      }

      // 自动上传
      if (onUpload && files.length > 0) {
        await onUpload(files);
      }

      // 清理 DOM
      cleanup();
    };
    const cleanup = () => {
      input.remove(); // 移除 input
    };
    input.click();
  };
  public async upload(
    {
      file,
      retryId,
      onProgress
    }: {
      file: File;
      onProgress: (id: string, progress: Partial<ProgressData>) => void;
      retryId?: string;
    },
    extra?: ExtraParams
  ) {
    const id = retryId || FileUpload.getUniqueId();
    if (!retryId) {
      if (FileUpload.isImage(file.type)) {
        const initLink = await FileUpload.getLocalImgLink(file);
        onProgress(id, FileUpload.getInitProgressData(id, file, initLink));
      } else {
        onProgress(id, FileUpload.getInitProgressData(id, file));
      }
    }
    const { success, message } = this.options.validate(file);
    if (!success) {
      onProgress(id, {
        isFinish: false,
        isError: true,
        errorMessage: message
      });
      return;
    }
    try {
      const res = await this.uploadInstance.upload({
        id,
        file,
        timeout: FileUpload.MAX_TIMEOUT,
        metadata: () => this.options.getTokenApi(extra),
        onProgress: (event: EnhancedProgressEvent<UploadMetadata>) =>
          this.onProgressHandle(id, event, onProgress)
      });
      const { isOk, data } = await this.options.finishUploadApi(
        {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          uploadToken: res.metadata.uploadToken
        },
        extra
      );
      if (!isOk) throw new Error('token feedback is fail');
      onProgress(id, {
        isFinish: true,
        isError: false,
        isCancel: false,
        link: data.url,
        resourceId: data.resourceId
      });
    } catch (e: any) {
      onProgress(id, {
        isFinish: false,
        isError: true,
        errorMessage: FileUpload.getErrorMessage(e),
        isCancel: e?.message?.startsWith('Cancel')
      });
    }
  }
}
